import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { useFiliera } from "@/context/FilieraContext";
import {
  Coins, Buildings, ClipboardText, WarningCircle, CheckCircle, TreeStructure
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, CartesianGrid
} from "recharts";

const COLORS = ["#0047FF", "#0F172A", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0891B2"];

export default function Dashboard() {
  const { selectedFilieraId, selectedFiliera } = useFiliera();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedFilieraId) {
      setStats(null);
      setError("");
      return;
    }

    let active = true;
    setStats(null);
    setError("");
    api.get("/dashboard/stats", { params: { filiera_id: selectedFilieraId } })
      .then((r) => { if (active) setStats(r.data); })
      .catch((e) => {
        if (active) setError(e.response?.data?.detail || e.message || "Impossibile caricare la dashboard");
      });

    return () => { active = false; };
  }, [selectedFilieraId]);

  if (!selectedFilieraId) {
    return (
      <div className="fade-in" data-testid="dashboard-page">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-8 sticky top-0 z-30">
          <div>
            <div className="eyebrow">Analytics</div>
            <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Cruscotto rendicontazione</h1>
          </div>
        </header>
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center" data-testid="dashboard-neutral-state">
            <TreeStructure size={36} weight="duotone" className="mx-auto text-slate-400" />
            <h2 className="mt-4 text-lg font-semibold text-slate-900">Seleziona una filiera attiva</h2>
            <p className="mt-2 text-sm text-slate-500 max-w-xl mx-auto">
              Usa il selettore “Filiera attiva” nella barra laterale per visualizzare KPI, avanzamento, anomalie e riepiloghi del progetto scelto.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) return <div className="p-10 text-red-600">{error}</div>;
  if (!stats) return <div className="p-10 text-slate-500">Caricamento…</div>;

  const kpi = [
    { label: "Filiere attive", value: stats.counts.filiere, icon: TreeStructure, delta: "In gestione" },
    { label: "Beneficiari", value: stats.counts.beneficiari, icon: Buildings, delta: "Aziende iscritte" },
    { label: "Investimenti", value: stats.counts.investimenti, icon: Coins, delta: "Approvati" },
    { label: "Documenti", value: stats.counts.documenti, icon: ClipboardText, delta: "Registrati" },
  ];

  return (
    <div className="fade-in" data-testid="dashboard-page">
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-30">
        <div>
          <div className="eyebrow">Analytics</div>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Cruscotto rendicontazione</h1>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Filiera attiva</div>
          <div className="text-sm font-semibold text-slate-800" data-testid="dashboard-active-filiera">{selectedFiliera?.nome || selectedFilieraId}</div>
        </div>
      </header>

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpi.map((k) => (
            <div key={k.label} className="stat-card" data-testid={`kpi-${k.label.toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label">{k.label}</div>
                  <div className="value">{k.value}</div>
                  <div className="delta">{k.delta}</div>
                </div>
                <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center text-slate-700">
                  <k.icon size={20} weight="duotone" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="stat-card lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label">Avanzamento Rendicontazione</div>
                <div className="value">{money(stats.importi.rendicontato)}</div>
                <div className="delta">
                  su {money(stats.importi.approvato)} approvati · residuo {money(stats.importi.residuo)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-blue-600" style={{ fontFamily: "Chivo" }}>
                  {stats.importi.percentuale.toFixed(1)}%
                </div>
                <div className="delta">completato</div>
              </div>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${Math.min(stats.importi.percentuale, 100)}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-200">
              <div>
                <div className="eyebrow">Contributo</div>
                <div className="text-lg font-bold mt-1">{money(stats.importi.contributo)}</div>
              </div>
              <div>
                <div className="eyebrow">Rendicontato</div>
                <div className="text-lg font-bold mt-1 text-emerald-600">{money(stats.importi.rendicontato)}</div>
              </div>
              <div>
                <div className="eyebrow">Residuo</div>
                <div className="text-lg font-bold mt-1 text-amber-600">{money(stats.importi.residuo)}</div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 mb-4">
              <WarningCircle size={20} weight="fill" className="text-amber-600" />
              <div className="label">Anomalie & Controlli</div>
            </div>
            {stats.anomalie.length === 0 ? (
              <div className="flex items-center gap-2 py-6 text-emerald-700">
                <CheckCircle size={24} weight="fill" />
                <span className="text-sm">Nessuna anomalia rilevata</span>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.anomalie.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <WarningCircle size={18} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-slate-700">{a.msg}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="stat-card">
            <div className="eyebrow mb-4">Importi per tabella</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.by_tabella}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="tabella" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="importo" fill="#0047FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="stat-card">
            <div className="eyebrow mb-4">Ripartizione per categoria</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={stats.by_categoria} dataKey="importo" nameKey="categoria" cx="50%" cy="50%" outerRadius={90} label>
                  {stats.by_categoria.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {stats.by_beneficiario.length > 0 && (
          <div className="stat-card">
            <div className="eyebrow mb-4">Riepilogo per beneficiario</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Beneficiario</th>
                  <th className="text-right">Approvato</th>
                  <th className="text-right">Rendicontato</th>
                  <th className="text-right">Residuo</th>
                  <th>Avanzamento</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_beneficiario.map((b) => {
                  const pct = b.approvato ? (b.rendicontato / b.approvato) * 100 : 0;
                  return (
                    <tr key={b.id} data-testid={`ben-row-${b.id}`}>
                      <td className="font-medium">{b.ragione_sociale}</td>
                      <td className="text-right">{money(b.approvato)}</td>
                      <td className="text-right text-emerald-600">{money(b.rendicontato)}</td>
                      <td className="text-right text-amber-600">{money(Math.max(b.approvato - b.rendicontato, 0))}</td>
                      <td className="w-64">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600" style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 w-12 text-right">{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
